import type { RecordRow } from './api';
import { creditLine, type Credit, type CreditGroup, type ResumeBlock } from './resume';

// ===================================================================
// W3 — A RESUME THE PERSON SHAPED, SAVED SO IT CAN BE SENT AGAIN.
// ===================================================================
//
// lib/resume.ts assembles the career in one canonical order and that stays
// the default on every tier. It is not what gets SENT. A stage submission
// wants theatre at the top; a musical wants the singing training on the first
// page; a commercial agent does not want six student films. So a person
// promotes, reorders and turns rows off — and then has to do the whole thing
// again next week, which is the part that makes it not worth doing at all.
//
// `aa_resume_version` is the memory of that arrangement, WITH ITS OWN
// HEADSHOT, and this module is its shape and its resolution. Decision
// `7c04876f`; gated `resume.editor` (Working+).
//
// -------------------------------------------------------------------
// THE ONE LAW: A VERSION IS A VIEW, NEVER A COPY
// -------------------------------------------------------------------
// The stored layout holds RECORD IDS and section order. It never holds the
// words on the page. Fix a misspelled production name on the engagement and
// every saved version says the new name the next time it is opened, because
// this app keeps one copy of a fact — the same discipline that forbids an
// "add a credit" button three files over.
//
// THREE CONSEQUENCES, ALL DELIBERATE:
//
//   1. A DELETED RECORD IS SKIPPED, never repaired and never rendered as a
//      gap. A saved arrangement has no authority over what still exists, and
//      a resume line reading "—" is worse than one credit fewer on the one
//      page an actor hands to a stranger (assembleCredits' own rule).
//   2. A RECORD MADE AFTER THE VERSION WAS SAVED IS **UNPLACED**. It appears
//      in the editor's tray and NOWHERE on the sheet. This is the whole
//      reason placement is stored as an inclusion list rather than an
//      exclusion list: a booking that lands on Tuesday must not silently
//      insert itself into the resume somebody sent on Monday. A version says
//      what it holds; anything else is a thing the person has not decided
//      about yet.
//   3. THE SECTION LABEL IS STORED. Group membership is DERIVED in
//      lib/resume.ts (a project's `kind` decides Film vs Theatre), so a
//      corrected field would otherwise re-title a saved document underneath
//      its owner. What they arranged is what prints.

/** The layout schema version. Written on every save and checked on every
 * read: a version saved by a future client with a shape this one cannot
 * resolve must be REFUSED loudly, not half-rendered. */
export const LAYOUT_VERSION = 1;

export interface LayoutSection {
  /** Stable id — `cast:Film`, `crew:Lighting`, `block:training`. Built by
   * `sectionIdFor*` below and never parsed back apart: the label rides
   * alongside precisely so nothing has to. */
  id: string;
  /** The printed heading. Stored, not derived — see consequence 3 above. */
  label: string;
  /** The record ids this section holds, in the order it prints them.
   * A credit is keyed by its ENGAGEMENT id (the record that means somebody
   * actually worked); a block row by its own record id. */
  records: string[];
  /** Ids from `records` the person turned OFF. Still placed, still visible in
   * the editor, absent from the sheet — which is what makes turning one back
   * on a single tap rather than a re-arrangement. */
  excluded?: string[];
}

export interface ResumeVersionLayout {
  v: number;
  sections: LayoutSection[];
}

/** One line on the sheet, whichever half of the resume it came from.
 *
 * ONE SHAPE FOR BOTH, on purpose. A credit and a training row are three
 * columns of text in the same grid (`.rz__row`), and a saved version renders
 * them through one path — so the typography cannot drift between the half of
 * a document that was assembled and the half that was typed. */
export interface VersionRow {
  id: string;
  lead: string;
  middle: string;
  trail: string;
  /** The person turned this row off. The editor shows it; the sheet does not. */
  excluded: boolean;
}

/** Everything a version COULD draw on, right now, keyed by record id — plus
 * where each row would naturally sit. Built fresh from the live records on
 * every render, which is what makes a version a view. */
export interface ResumeCatalogue {
  rows: Map<string, { row: VersionRow; sectionId: string; sectionLabel: string }>;
  /** The sections the automatic resume would print, in its own order. The
   * seed for "create from the current layout" and the home an unplaced row
   * is placed into. */
  sections: { id: string; label: string; records: string[] }[];
}

export function sectionIdForGroup(group: CreditGroup): string {
  return `${group.side}:${group.group}`;
}

export function sectionIdForBlock(block: ResumeBlock): string {
  return `block:${block.key}`;
}

/** A credit as a row. Same three columns PerformerResume already prints for
 * the automatic resume, so a saved version and the automatic one read
 * identically — they are the same document, differently ordered. */
function rowFromCredit(credit: Credit): VersionRow {
  return {
    id: credit.engagementId,
    // The automatic sheet renders a missing production as an em dash rather
    // than dropping the line; a saved version must not disagree with it.
    lead: credit.production ?? '—',
    middle: credit.role ?? '',
    trail: creditLine(credit),
    excluded: false,
  };
}

/**
 * THE LIVE CATALOGUE. `groups` is BOTH halves of the career — pass
 * `[...castResume(credits), ...crewResume(credits)]`, not one variant.
 *
 * ⚑ A SAVED VERSION IS NOT A VARIANT. The automatic resume offers Cast / Crew
 * / Everything because it must choose for you; a version is a document a
 * person built, and "the theatre one" may perfectly well hold two departments
 * and no film at all. Narrowing the catalogue to one variant would make whole
 * groups un-placeable and the tray would never mention them, which is the
 * silent kind of missing.
 */
export function catalogueFrom(groups: CreditGroup[], blocks: ResumeBlock[]): ResumeCatalogue {
  const rows: ResumeCatalogue['rows'] = new Map();
  const sections: ResumeCatalogue['sections'] = [];

  for (const group of groups) {
    const id = sectionIdForGroup(group);
    const ids: string[] = [];
    for (const credit of group.credits) {
      rows.set(credit.engagementId, { row: rowFromCredit(credit), sectionId: id, sectionLabel: group.group });
      ids.push(credit.engagementId);
    }
    sections.push({ id, label: group.group, records: ids });
  }

  for (const block of blocks) {
    const id = sectionIdForBlock(block);
    const ids: string[] = [];
    for (const r of block.rows) {
      rows.set(r.id, {
        row: { id: r.id, lead: r.lead ?? '', middle: r.middle ?? '', trail: r.trail ?? '', excluded: false },
        sectionId: id,
        sectionLabel: block.label,
      });
      ids.push(r.id);
    }
    sections.push({ id, label: block.label, records: ids });
  }

  return { rows, sections };
}

/** The seed a new version starts from: everything the automatic resume prints
 * today, in its own order, nothing turned off.
 *
 * ⚑ "CREATE FROM THE CURRENT AUTOMATIC LAYOUT" IS THE ONLY CREATE DOOR, and
 * that is the design rather than a shortcut. A blank version is a blank page
 * with a reorder tool on it — nobody's starting point. A copy of what you
 * already have, which you then push around, is. */
export function layoutFromCatalogue(cat: ResumeCatalogue): ResumeVersionLayout {
  return {
    v: LAYOUT_VERSION,
    sections: cat.sections
      // A section with nothing in it is not seeded. It would print as a rule
      // with white space under it, which is the same defect resumeBlocks()
      // refuses one file over.
      .filter((s) => s.records.length > 0)
      .map((s) => ({ id: s.id, label: s.label, records: [...s.records] })),
  };
}

/** Why a stored layout could not be used. Named rather than collapsed to
 * null, because the two mean opposite things to a reader: `absent` is a
 * version saved before anything was arranged (open it and it is empty, which
 * is honest), `unreadable` is a shape this client does not understand and
 * must NOT guess at. */
export type LayoutFault = 'absent' | 'unreadable';

export interface ParsedLayout {
  layout: ResumeVersionLayout | null;
  fault: LayoutFault | null;
}

/**
 * A stored `layout` field, validated.
 *
 * ⚠ EVERY BRANCH IS CHECKED, because this value comes back from a JSON column
 * and TypeScript knows nothing about what is actually in it. A half-trusted
 * parse would render a version with sections silently missing, which looks
 * exactly like a version somebody arranged that way.
 *
 * A FUTURE `v` IS A REFUSAL, not a best effort. If a later client adds a
 * field this one cannot honour, quietly ignoring it prints a document that is
 * not the one its owner saved — on the page they hand to a stranger.
 */
export function parseLayout(value: unknown): ParsedLayout {
  if (value === null || value === undefined) return { layout: null, fault: 'absent' };
  if (typeof value !== 'object' || Array.isArray(value)) return { layout: null, fault: 'unreadable' };

  const raw = value as Record<string, unknown>;
  if (typeof raw.v !== 'number' || raw.v > LAYOUT_VERSION) return { layout: null, fault: 'unreadable' };
  if (!Array.isArray(raw.sections)) return { layout: null, fault: 'unreadable' };

  const sections: LayoutSection[] = [];
  for (const entry of raw.sections) {
    if (typeof entry !== 'object' || entry === null) return { layout: null, fault: 'unreadable' };
    const sec = entry as Record<string, unknown>;
    if (typeof sec.id !== 'string' || typeof sec.label !== 'string') return { layout: null, fault: 'unreadable' };
    if (!Array.isArray(sec.records) || sec.records.some((r) => typeof r !== 'string')) {
      return { layout: null, fault: 'unreadable' };
    }
    const excluded = sec.excluded;
    if (excluded !== undefined && (!Array.isArray(excluded) || excluded.some((r) => typeof r !== 'string'))) {
      return { layout: null, fault: 'unreadable' };
    }
    sections.push({
      id: sec.id,
      label: sec.label,
      records: sec.records as string[],
      ...(excluded === undefined ? {} : { excluded: excluded as string[] }),
    });
  }
  return { layout: { v: raw.v, sections }, fault: null };
}

export interface ResolvedSection {
  id: string;
  label: string;
  /** Placed rows that still exist, in the saved order, carrying their
   * excluded flag. */
  rows: VersionRow[];
}

export interface ResolvedVersion {
  sections: ResolvedSection[];
  /** Live rows the layout does not name at all — the EDITOR's tray, and
   * NEVER the sheet. See consequence 2 in the header. */
  unplaced: { row: VersionRow; sectionId: string; sectionLabel: string }[];
}

/**
 * The saved arrangement, resolved against what exists RIGHT NOW.
 *
 * ⚠ A DELETED ID IS SKIPPED, never rendered. `cat.rows` is built from the
 * live, non-retired records, so an id the layout still names and the
 * catalogue does not is a record that has gone — archived, deleted, or
 * (for a credit) an engagement that fell out of the credit states. It leaves
 * no trace on the page.
 *
 * ⚠ A SECTION WHOSE ROWS ALL VANISHED IS DROPPED. A heading over nothing is
 * the defect resumeVariants() and resumeBlocks() both already refuse.
 *
 * ⚠ AN ID NAMED TWICE IS PLACED ONCE, in the first section that claims it. A
 * layout should never contain one, but a duplicate on a resume reads as a
 * person padding their credits, which is the single worst thing this page
 * could do to its owner.
 */
export function resolveVersion(layout: ResumeVersionLayout | null, cat: ResumeCatalogue): ResolvedVersion {
  const placed = new Set<string>();
  const sections: ResolvedSection[] = [];

  for (const section of layout?.sections ?? []) {
    const excluded = new Set(section.excluded ?? []);
    const rows: VersionRow[] = [];
    for (const id of section.records) {
      if (placed.has(id)) continue;
      const live = cat.rows.get(id);
      if (!live) continue;
      placed.add(id);
      rows.push({ ...live.row, excluded: excluded.has(id) });
    }
    if (rows.length > 0) sections.push({ id: section.id, label: section.label, rows });
  }

  const unplaced: ResolvedVersion['unplaced'] = [];
  // Walked in CATALOGUE order rather than Map insertion order by accident:
  // the catalogue's order is the automatic resume's, so the tray reads
  // newest-first inside each group exactly like the sheet does.
  for (const section of cat.sections) {
    for (const id of section.records) {
      if (placed.has(id)) continue;
      const live = cat.rows.get(id);
      if (!live) continue;
      unplaced.push({ row: live.row, sectionId: live.sectionId, sectionLabel: live.sectionLabel });
    }
  }

  return { sections, unplaced };
}

/** What actually PRINTS: the resolved sections with every turned-off row
 * removed, and any section left empty by that removal dropped.
 *
 * SEPARATE FROM resolveVersion ON PURPOSE. The editor needs the excluded
 * rows — that is how you turn one back on — and the sheet must never see
 * them. One function returning both audiences' answer is how a control ends
 * up printed on a resume. */
export function printableSections(resolved: ResolvedVersion): ResolvedSection[] {
  return resolved.sections
    .map((s) => ({ ...s, rows: s.rows.filter((r) => !r.excluded) }))
    .filter((s) => s.rows.length > 0);
}

// -------------------------------------------------------------------
// THE EDITS. Every one is PURE — it takes a layout and returns a new one,
// and nothing here touches the server. The screen saves the result, so a
// person can push six things around and press Save once; and a save that
// fails leaves the arrangement on screen rather than half-applied.
// -------------------------------------------------------------------

/**
 * Move a section one place up (-1) or down (+1).
 *
 * ⚑ KEYED BY SECTION ID, NEVER BY THE INDEX THE EDITOR IS RENDERING.
 * `resolveVersion` DROPS a section whose records have all gone, so the list a
 * person is looking at and the list stored in the layout can differ by any
 * number of entries — and an index taken from the visible list would then
 * move a DIFFERENT section, silently, on somebody's resume. Found by reading
 * the two lists side by side rather than by a failing test, which is why
 * there is now one (see 'moves the section a person pointed at').
 *
 * A move off either end is a NO-OP that returns the SAME object, so a caller
 * can compare by identity and avoid marking an untouched layout dirty.
 */
export function moveSection(layout: ResumeVersionLayout, sectionId: string, delta: -1 | 1): ResumeVersionLayout {
  const index = layout.sections.findIndex((s) => s.id === sectionId);
  const to = index + delta;
  if (index < 0 || to < 0 || to >= layout.sections.length) return layout;
  const sections = [...layout.sections];
  const [moved] = sections.splice(index, 1);
  sections.splice(to, 0, moved!);
  return { ...layout, sections };
}

/** Turn one placed row off, or back on. */
export function toggleRow(layout: ResumeVersionLayout, sectionId: string, recordId: string): ResumeVersionLayout {
  return {
    ...layout,
    sections: layout.sections.map((s) => {
      if (s.id !== sectionId) return s;
      const excluded = new Set(s.excluded ?? []);
      if (excluded.has(recordId)) excluded.delete(recordId);
      else excluded.add(recordId);
      // The key is DROPPED rather than left as [] when nothing is excluded,
      // so a layout that has been toggled twice is byte-identical to one that
      // never was — which is what keeps "is this dirty?" answerable.
      const next: LayoutSection = { id: s.id, label: s.label, records: s.records };
      if (excluded.size > 0) next.excluded = [...excluded];
      return next;
    }),
  };
}

/**
 * Put an unplaced row onto the sheet, in the section it naturally belongs to.
 *
 * The section is APPENDED if the layout has never held it — a first training
 * record on a version made before there was any training has nowhere else to
 * go, and appending is the honest place: at the end, where the person can see
 * it and move it, rather than inserted somewhere that pretends they chose it.
 */
export function placeRow(layout: ResumeVersionLayout, cat: ResumeCatalogue, recordId: string): ResumeVersionLayout {
  const live = cat.rows.get(recordId);
  if (!live) return layout;
  const existing = layout.sections.find((s) => s.id === live.sectionId);
  if (existing) {
    if (existing.records.includes(recordId)) return layout;
    return {
      ...layout,
      sections: layout.sections.map((s) =>
        s.id === live.sectionId ? { ...s, records: [...s.records, recordId] } : s
      ),
    };
  }
  return {
    ...layout,
    sections: [...layout.sections, { id: live.sectionId, label: live.sectionLabel, records: [recordId] }],
  };
}

/** The versions a performer has, oldest first — the order they were made,
 * which is the only order that means anything about a set of arrangements
 * nobody ranked. A version with no name falls back to the record's own
 * display name rather than rendering blank. */
export function versionName(record: RecordRow): string {
  const raw = record.fields.name;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : record.display_name;
}
